import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import toast from '../../utils/notifications';

const MenuManagement = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Fetch all items on mount, category filter will be applied after fetch
    fetchMenuItems('', selectedCategory, 'all');
  }, [selectedCategory, availabilityFilter]);

  useEffect(() => {
    // Fetch categories and initial items on mount
    fetchMenuItemsAndCategories();
  }, []);

  const fetchMenuItemsAndCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/menu-items', { params: { per_page: 10000 } });
      const items = response.data.data.data || response.data.data || [];
      
      // Extract unique categories from items
      const uniqueCategories = [...new Set(items.map(item => item.category).filter(Boolean))];
      setCategories(uniqueCategories.sort());
      
      // Set initial items
      setMenuItems(Array.isArray(items) ? items : []);
      console.log('Fetched items:', items); // Debug log
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to fetch menu items');
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async (search = '', category = '', filter = 'all') => {
    try {
      setLoading(true);
      const params = { per_page: 10000 };
      
      // Use 'q' parameter for search (as per backend API)
      if (search && search.trim()) {
        params.q = search.trim();
      }
      
      // Use 'category' parameter for category filter
      if (category && category.trim()) {
        params.category = category.trim();
      }

      console.log('Fetching with params:', params); // Debug log
      const response = await api.get('/menu-items', { params });

      let items = response.data.data.data || response.data.data || [];
      items = Array.isArray(items) ? items : [];
      
      // Apply status filter on frontend since backend doesn't support it
      if (filter !== 'all') {
        items = items.filter(item => item.status === filter);
      }
      
      setMenuItems(items);
      console.log('Fetched and filtered items:', items); // Debug log
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to fetch menu items');
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const updateItemStatus = async (menuItemId, newStatus) => {
    try {
      // Use standard PUT endpoint to update menu item
      await api.put(`/menu-items/${menuItemId}`, { 
        status: newStatus,
        is_available: newStatus === 'available'
      });
      
      // Update local state
      setMenuItems(menuItems.map(item => 
        item.menu_item_id === menuItemId 
          ? { ...item, status: newStatus, is_available: newStatus === 'available' }
          : item
      ));
      
      toast.success(`Menu item marked as ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update menu item status');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Fetch only when Search button is clicked
    fetchMenuItems(searchTerm, selectedCategory, availabilityFilter);
  };

  const getStatusBadge = (status) => {
    if (status === 'available') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-4 h-4 mr-1" />
          Available
        </span>
      );
    } else if (status === 'sold_out') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="w-4 h-4 mr-1" />
          Sold Out
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <XCircleIcon className="w-4 h-4 mr-1" />
        Unavailable
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Menu Management</h2>
        <p className="mt-1 text-xs sm:text-sm text-gray-600">
          Manage menu items and mark items as available or sold out
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow-sm rounded-lg p-3 sm:p-6">
        <form onSubmit={handleSearch} className="space-y-3 sm:space-y-4">
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search menu items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                />
              </div>
            </div>
            <button
              type="submit"
              className="px-3 sm:px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors text-sm whitespace-nowrap"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Filters</span>
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-4 border-t">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Status Filter
                </label>
                <select
                  value={availabilityFilter}
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                >
                  <option value="all">All Items</option>
                  <option value="available">Available Only</option>
                  <option value="sold_out">Sold Out Only</option>
                  <option value="unavailable">Unavailable Only</option>
                </select>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Total Items</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{menuItems.length}</div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Available</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-green-600">
            {menuItems.filter(item => item.status === 'available').length}
          </div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Sold Out</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">
            {menuItems.filter(item => item.status === 'sold_out').length}
          </div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Unavailable</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-gray-600">
            {menuItems.filter(item => item.status === 'unavailable').length}
          </div>
        </div>
      </div>

      {/* Menu Items Table/Cards */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {/* Desktop/Tablet Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item Name
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="hidden lg:table-cell px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {menuItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 lg:px-6 py-12 text-center text-sm text-gray-500">
                    No menu items found
                  </td>
                </tr>
              ) : (
                menuItems.map((item) => (
                  <tr key={item.menu_item_id} className="hover:bg-gray-50">
                    <td className="px-4 lg:px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500 line-clamp-1">{item.description}</div>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <span className="text-sm text-gray-900">{item.category}</span>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        ₱{parseFloat(item.price).toFixed(2)}
                      </span>
                    </td>
                    <td className="hidden lg:table-cell px-4 lg:px-6 py-4">
                      {getStatusBadge(item.status || (item.is_available ? 'available' : 'unavailable'))}
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <select
                        value={item.status || 'available'}
                        onChange={(e) => updateItemStatus(item.menu_item_id, e.target.value)}
                        className="w-full min-w-[120px] px-2 py-1.5 rounded border border-gray-300 bg-white text-xs font-medium text-gray-700 hover:border-gray-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors"
                      >
                        <option value="available">✓ Available</option>
                        <option value="sold_out">✗ Sold Out</option>
                        <option value="unavailable">⊘ Unavailable</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-gray-200">
          {menuItems.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-gray-500">
              No menu items found
            </div>
          ) : (
            menuItems.map((item, index) => (
              <div key={item.menu_item_id} className="p-4 hover:bg-gray-50">
                <div className="space-y-3">
                  {/* Header with name and status */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                      {item.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.description}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {getStatusBadge(item.status || (item.is_available ? 'available' : 'unavailable'))}
                    </div>
                  </div>
                  
                  {/* Category and Price */}
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {item.category}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      ₱{parseFloat(item.price).toFixed(2)}
                    </span>
                  </div>

                  {/* Status Update Dropdown */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Update Status:
                    </label>
                    <select
                      value={item.status || 'available'}
                      onChange={(e) => updateItemStatus(item.menu_item_id, e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:border-gray-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors shadow-sm"
                    >
                      <option value="available">✓ Available</option>
                      <option value="sold_out">✗ Sold Out</option>
                      <option value="unavailable">⊘ Unavailable</option>
                    </select>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuManagement;
